import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Item } from 'react-stately';
import {
  IconIosColor,
  IconAndroidColor,
  IconOsOutline,
} from '@saucelabs/ds-icons';
import { Filter } from '@saucelabs/ds-filters';

import actions from '../../actions';
import constants from '../../constants';
import selectors from '../../selectors';
import { CONFIGURATION_TEST_TYPES } from '../../../../constants';
import { trackEvent, MANUAL_EVENTS } from '../../../../tracking';
import { FilterUpdate } from './DeviceFilterBar';
import { ValueOf } from '../../../../globalTypes';

type PlatformSelection = ValueOf<typeof constants.MOBILE_PLATFORMS>;

const {
  MOBILE_SELECTION_FILTER,
  MOBILE_SELECTION_FILTER_CATEGORY: FILTER_CATEGORY,
} = constants;

const PlatformFilter: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const {
    activeFilters,
    testType,
    mobileAppConfiguration,
  } = useSelector(selectors.mobileSelectionSelector);
  const isAppTest = testType === CONFIGURATION_TEST_TYPES.APP;
  const isDisabled = !!mobileAppConfiguration && isAppTest;

  const selectedPlatform = activeFilters.get(FILTER_CATEGORY.OS);

  const filterIcon = (() => {
    const osKey = selectedPlatform.first();

    if (osKey === MOBILE_SELECTION_FILTER.IOS) {
      return IconIosColor as React.ReactSVGElement;
    }

    if (osKey === MOBILE_SELECTION_FILTER.ANDROID) {
      return IconAndroidColor as React.ReactSVGElement;
    }

    return IconOsOutline as React.ReactSVGElement;
  })();

  const selectionChangedHandler = useCallback((newValues: Set<PlatformSelection>) => {
    let newFilters: FilterUpdate = [{
      category: FILTER_CATEGORY.OS,
      values: [...newValues],
    }];
    // Remove filters that might not exist for the selected platform
    if (newValues.size > 0) {
      newFilters = newFilters.concat([{
        category: FILTER_CATEGORY.OS_VERSION,
        values: [],
      }, {
        category: FILTER_CATEGORY.MANUFACTURER,
        values: [],
      }]);
    }
    dispatch(actions.setFilters(newFilters));

    trackEvent(MANUAL_EVENTS.FILTER_REAL_DEVICE, {
      filterCategory: FILTER_CATEGORY.OS,
      filterValues: [...newValues],
      testType,
    });
  }, [dispatch, testType]);

  return (
    <Filter
      data-qa="device-filter-bar-platform"
      isDisabled={isDisabled}
      label="All Platforms"
      name="platform"
      onSelectionChange={selectionChangedHandler}
      prefixIcon={filterIcon}
      selectedKeys={selectedPlatform}
      selectionMode="single"
      type="listbox"
    >
      <Item
        key={MOBILE_SELECTION_FILTER.ANDROID}
        textValue="Android"
      >
        <div className="flex items-center gap-x-1">
          <IconAndroidColor className="w-2 h-2" />
          Android
        </div>
      </Item>
      <Item
        key={MOBILE_SELECTION_FILTER.IOS}
        textValue="iOS"
      >
        <div className="flex items-center gap-x-1">
          <IconIosColor className="w-2 h-2" />
          iOS
        </div>
      </Item>
    </Filter>
  );
};

export default PlatformFilter;
